<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';
    import { buildCapacityQuery, formatActiveTeacherCount, formatDuration, sortTeachersByAvailability } from './teacherAvailabilityCapacity.js';

    const apiBase = getApiBase();
    let report = null;
    let filterOptions = { teachers: [], subjects: [], grades: [] };
    let teacherId = '';
    let subjectId = '';
    let grade = '';
    let loading = true;
    let errorMessage = '';
    let expandedTeacherId = null;

    $: teachers = sortTeachersByAvailability(report?.teachers || []);
    $: maxWeekdayMinutes = Math.max(1, ...(report?.weekdays || []).map(day => day.submitted_minutes || 0));

    onMount(loadCapacity);

    async function loadCapacity() {
        loading = true;
        errorMessage = '';
        const query = buildCapacityQuery({ teacherId, subjectId, grade });
        try {
            const response = await fetch(`${apiBase}/studentplayground/api/admin/teacher-availability-capacity/${query}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.status === 401) localStorage.removeItem('jwtToken');
            if (!response.ok) throw new Error(data.error || 'Could not load teacher availability.');
            report = data;
            filterOptions = data.filters || filterOptions;
            if (!data.teachers?.some(teacher => teacher.teacher_id === expandedTeacherId)) expandedTeacherId = null;
        } catch (error) {
            errorMessage = error.message || 'Could not load teacher availability.';
        } finally {
            loading = false;
        }
    }

    function resetFilters() {
        teacherId = '';
        subjectId = '';
        grade = '';
        loadCapacity();
    }

    function monthLabel(period) {
        if (!period?.start_date) return '';
        return new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric', timeZone: 'UTC' })
            .format(new Date(`${period.start_date}T00:00:00Z`));
    }

    function dateLabel(row) {
        return new Intl.DateTimeFormat('en', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' })
            .format(new Date(`${row.date}T00:00:00Z`));
    }

    function percentage(value, total) {
        if (!total) return 0;
        return Math.max(0, Math.min(100, (value / total) * 100));
    }
</script>

<section class="capacity" aria-labelledby="teacher-capacity-title">
    <header class="heading">
        <div>
            <p class="eyebrow">Monthly capacity overview</p>
            <h3 id="teacher-capacity-title">Teacher availability capacity</h3>
            <p>See submitted teaching time, scheduled classes, and every remaining open interval in one place.</p>
        </div>
        {#if report}<span class="month-badge">{monthLabel(report.period)}</span>{/if}
    </header>

    <div class="filters" aria-label="Capacity filters">
        <label>Teacher
            <select bind:value={teacherId} on:change={loadCapacity}>
                <option value="">All teachers</option>
                {#each filterOptions.teachers as teacher}<option value={teacher.id}>{teacher.name}</option>{/each}
            </select>
        </label>
        <label>Subject
            <select bind:value={subjectId} on:change={loadCapacity}>
                <option value="">All subjects</option>
                {#each filterOptions.subjects as subject}<option value={subject.id}>{subject.name}</option>{/each}
            </select>
        </label>
        <label>Grade
            <select bind:value={grade} on:change={loadCapacity}>
                <option value="">Any grade</option>
                {#each filterOptions.grades as item}<option value={item}>Grade {item}</option>{/each}
            </select>
        </label>
        <button class="reset" type="button" on:click={resetFilters} disabled={!teacherId && !subjectId && !grade}>Reset filters</button>
    </div>

    {#if loading && !report}
        <div class="loading" role="status"><span></span><span></span><span></span><p>Calculating monthly availability…</p></div>
    {:else if errorMessage}
        <div class="error" role="alert">
            <strong>Availability could not be loaded.</strong><span>{errorMessage}</span>
            <button type="button" on:click={loadCapacity}>Try again</button>
        </div>
    {:else if report}
        <div class:refreshing={loading} aria-busy={loading}>
            <div class="summary-grid">
                <article class="summary-card available-card">
                    <span class="card-label">Remaining availability</span>
                    <strong>{report.summary.available_interval_count} open intervals</strong>
                    <span class="large-duration">{formatDuration(report.summary.available_minutes)}</span>
                </article>
                <article class="summary-card">
                    <span class="card-label">Teachers with open time</span>
                    <strong>{report.summary.teachers_with_availability} of {report.summary.teacher_count}</strong>
                    <span>{report.summary.teacher_count - report.summary.teachers_with_availability} without open time</span>
                    {#if report.comparison?.previous_academic_year}
                        <div class="historical-comparison">
                            <span>Previous academic year</span>
                            <strong>{formatActiveTeacherCount(report.comparison.previous_academic_year.active_teacher_count)} in {monthLabel(report.comparison.previous_academic_year)}</strong>
                            <small>Taught at least one class</small>
                        </div>
                    {/if}
                </article>
                <article class="summary-card">
                    <span class="card-label">Submitted time</span>
                    <strong>{formatDuration(report.summary.submitted_minutes)}</strong>
                    <span>Across the selected teachers</span>
                </article>
                <article class="summary-card occupied-card">
                    <span class="card-label">Occupied time</span>
                    <strong>{formatDuration(report.summary.occupied_minutes)}</strong>
                    <span>Classes inside submitted windows</span>
                </article>
            </div>

            <section class="weekday-section" aria-labelledby="weekday-capacity-title">
                <div class="section-heading">
                    <div><h4 id="weekday-capacity-title">Capacity by weekday</h4><p>Totals across every occurrence in {monthLabel(report.period)}.</p></div>
                    <div class="legend" aria-label="Chart legend">
                        <span><i class="submitted-dot"></i>Submitted</span>
                        <span><i class="occupied-dot"></i>Occupied</span>
                        <span><i class="available-dot"></i>Available</span>
                    </div>
                </div>
                <div class="weekday-list">
                    {#each report.weekdays as day}
                        <div class="weekday-row">
                            <div class="weekday-name"><strong>{day.day_name}</strong><span>{day.available_interval_count} intervals · {formatDuration(day.available_minutes)} free</span></div>
                            <div class="bar-track" title={`${day.day_name}: ${formatDuration(day.available_minutes)} available`}>
                                <div class="bar-total" style={`width: ${percentage(day.submitted_minutes, maxWeekdayMinutes)}%`}>
                                    <span class="bar-occupied" style={`width: ${percentage(day.occupied_minutes, day.submitted_minutes)}%`}></span>
                                    <span class="bar-available" style={`width: ${percentage(day.available_minutes, day.submitted_minutes)}%`}></span>
                                </div>
                            </div>
                            <span class="weekday-total">{formatDuration(day.submitted_minutes)}</span>
                        </div>
                    {/each}
                </div>
            </section>

            <section class="teachers-section" aria-labelledby="teacher-breakdown-title">
                <div class="section-heading">
                    <div><h4 id="teacher-breakdown-title">Teacher breakdown</h4><p>Ordered by the most remaining time.</p></div>
                </div>
                {#if teachers.length === 0}
                    <div class="empty"><strong>No matching teachers</strong><span>Change or reset the filters to see monthly availability.</span></div>
                {:else}
                    <div class="teacher-list">
                        {#each teachers as teacher}
                            <article class:zero-capacity={!teacher.available_minutes} class="teacher-card">
                                <button
                                    class="teacher-toggle"
                                    type="button"
                                    aria-expanded={expandedTeacherId === teacher.teacher_id}
                                    aria-controls={`capacity-teacher-${teacher.teacher_id}`}
                                    on:click={() => expandedTeacherId = expandedTeacherId === teacher.teacher_id ? null : teacher.teacher_id}
                                >
                                    <span class="teacher-identity"><strong>{teacher.teacher_name}</strong><small>{teacher.subjects.map(subject => `${subject.name} (${subject.grade})`).join(' · ') || 'No subject information'}</small></span>
                                    <span class="teacher-stat"><strong>{formatDuration(teacher.available_minutes)}</strong><small>{teacher.available_interval_count} open intervals</small></span>
                                    <span class="chevron" aria-hidden="true">{expandedTeacherId === teacher.teacher_id ? '▾' : '▸'}</span>
                                </button>
                                {#if expandedTeacherId === teacher.teacher_id}
                                    <div class="teacher-detail" id={`capacity-teacher-${teacher.teacher_id}`}>
                                        <div class="teacher-totals">
                                            <span><i class="submitted-dot"></i>Submitted <strong>{formatDuration(teacher.submitted_minutes)}</strong></span>
                                            <span><i class="occupied-dot"></i>Occupied <strong>{formatDuration(teacher.occupied_minutes)}</strong></span>
                                            <span><i class="available-dot"></i>Available <strong>{formatDuration(teacher.available_minutes)}</strong></span>
                                        </div>
                                        {#if teacher.dates.length === 0}
                                            <p class="no-slots">This teacher has no submitted availability for the month.</p>
                                        {:else}
                                            <div class="date-list">
                                                {#each teacher.dates as day}
                                                    <div class="date-row">
                                                        <div class="date-label"><strong>{dateLabel(day)}</strong><span>{formatDuration(day.available_minutes)} free</span></div>
                                                        <div class="time-chips">
                                                            {#each day.available_intervals as interval}
                                                                <span class="time-chip">{interval.start_time}–{interval.end_time}<small>{formatDuration(interval.duration_minutes)}</small></span>
                                                            {:else}
                                                                <span class="fully-booked">Fully occupied</span>
                                                            {/each}
                                                        </div>
                                                    </div>
                                                {/each}
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                            </article>
                        {/each}
                    </div>
                {/if}
            </section>

            <p class="footnote">Whole calendar month · Europe/Vilnius · Based on currently submitted recurring schedules. Updated {new Date(report.period.generated_at).toLocaleString()}.</p>
        </div>
    {/if}
</section>

<style>
    .capacity { --blue: #2563eb; --blue-soft: #dbeafe; --green: #15803d; --green-soft: #dcfce7; --red: #b91c1c; --red-soft: #fee2e2; color: #172033; width: 100%; }
    .heading, .section-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
    .eyebrow { margin: 0 0 .25rem; color: #2563eb; font-size: .75rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
    .heading h3 { margin: 0; font-size: clamp(1.45rem, 2.2vw, 2rem); }
    .heading p:not(.eyebrow), .section-heading p { margin: .4rem 0 0; color: #64748b; line-height: 1.5; }
    .month-badge { padding: .55rem .8rem; border: 1px solid #bfdbfe; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-weight: 750; white-space: nowrap; }
    .filters { display: grid; grid-template-columns: repeat(3, minmax(150px, 1fr)) auto; align-items: end; gap: .85rem; margin: 1.4rem 0; padding: 1rem; border: 1px solid #dbe3ec; border-radius: 14px; background: #f8fafc; }
    label { color: #334155; font-size: .82rem; font-weight: 750; }
    select { display: block; width: 100%; min-height: 42px; margin-top: .35rem; padding: .55rem .7rem; border: 1px solid #cbd5e1; border-radius: 8px; background: white; color: #172033; font-size: .95rem; }
    button { font: inherit; }
    button:focus-visible, select:focus-visible { outline: 3px solid rgba(37, 99, 235, .3); outline-offset: 2px; }
    .reset { min-height: 42px; padding: .6rem .9rem; border: 0; border-radius: 8px; background: #e2e8f0; color: #334155; cursor: pointer; font-weight: 750; }
    .reset:disabled { cursor: default; opacity: .5; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .9rem; }
    .summary-card { display: flex; flex-direction: column; min-height: 125px; padding: 1rem; border: 1px solid #dbe3ec; border-radius: 14px; background: white; box-shadow: 0 6px 20px rgba(15, 23, 42, .05); }
    .summary-card strong { margin: .55rem 0 .25rem; font-size: 1.45rem; }
    .summary-card > span:last-child { color: #64748b; font-size: .83rem; }
    .available-card { border-color: #86efac; background: linear-gradient(145deg, #f0fdf4, white); }
    .available-card strong, .large-duration { color: #166534; }
    .available-card .large-duration { font-size: 1.2rem; font-weight: 800; }
    .occupied-card { border-color: #fecaca; }
    .card-label { color: #475569; font-size: .78rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
    .historical-comparison { display: flex; flex-direction: column; gap: .12rem; margin-top: .8rem; padding-top: .65rem; border-top: 1px solid #e2e8f0; }
    .historical-comparison span { color: #475569; font-size: .7rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
    .historical-comparison strong { margin: 0; color: #334155; font-size: .86rem; line-height: 1.35; }
    .historical-comparison small { color: #64748b; font-size: .72rem; }
    .weekday-section, .teachers-section { margin-top: 1rem; padding: 1.1rem; border: 1px solid #dbe3ec; border-radius: 14px; background: white; }
    .section-heading h4 { margin: 0; font-size: 1.1rem; }
    .legend, .teacher-totals { display: flex; flex-wrap: wrap; gap: .8rem; color: #475569; font-size: .8rem; }
    .legend span, .teacher-totals span { display: inline-flex; align-items: center; gap: .35rem; }
    i { display: inline-block; width: 10px; height: 10px; border-radius: 3px; }
    .submitted-dot { background: var(--blue); } .occupied-dot { background: var(--red); } .available-dot { background: var(--green); }
    .weekday-list { display: grid; gap: .75rem; margin-top: 1rem; }
    .weekday-row { display: grid; grid-template-columns: 175px minmax(140px, 1fr) 65px; align-items: center; gap: .75rem; }
    .weekday-name { display: flex; flex-direction: column; }
    .weekday-name span { color: #64748b; font-size: .76rem; }
    .bar-track { height: 16px; overflow: hidden; border-radius: 999px; background: #eef2f7; }
    .bar-total { display: flex; height: 100%; min-width: 2px; overflow: hidden; border-radius: inherit; background: var(--blue-soft); }
    .bar-occupied { background: var(--red); } .bar-available { background: var(--green); }
    .weekday-total { color: #475569; font-size: .8rem; text-align: right; }
    .teacher-list { display: grid; gap: .65rem; margin-top: 1rem; }
    .teacher-card { overflow: hidden; border: 1px solid #dbe3ec; border-radius: 11px; }
    .teacher-card.zero-capacity { background: #f8fafc; }
    .teacher-toggle { display: grid; grid-template-columns: minmax(0, 1fr) auto 20px; align-items: center; gap: 1rem; width: 100%; padding: .9rem 1rem; border: 0; background: transparent; color: inherit; cursor: pointer; text-align: left; }
    .teacher-toggle:hover { background: #f8fafc; }
    .teacher-identity, .teacher-stat { display: flex; flex-direction: column; min-width: 0; }
    .teacher-identity small, .teacher-stat small { overflow: hidden; color: #64748b; font-size: .76rem; text-overflow: ellipsis; white-space: nowrap; }
    .teacher-stat { color: #166534; text-align: right; }
    .chevron { color: #64748b; font-size: 1.15rem; }
    .teacher-detail { padding: .85rem 1rem 1rem; border-top: 1px solid #e2e8f0; background: #f8fafc; }
    .teacher-totals strong { color: #172033; }
    .date-list { display: grid; gap: .55rem; margin-top: .85rem; }
    .date-row { display: grid; grid-template-columns: 150px 1fr; gap: .75rem; padding-top: .55rem; border-top: 1px solid #e2e8f0; }
    .date-label { display: flex; flex-direction: column; font-size: .85rem; }
    .date-label span { color: #64748b; font-size: .75rem; }
    .time-chips { display: flex; flex-wrap: wrap; gap: .4rem; }
    .time-chip { display: inline-flex; align-items: center; gap: .45rem; padding: .4rem .6rem; border: 1px solid #86efac; border-radius: 8px; background: var(--green-soft); color: #14532d; font-weight: 750; }
    .time-chip small { padding-left: .45rem; border-left: 1px solid #86efac; font-size: .7rem; font-weight: 600; }
    .fully-booked { padding: .35rem .55rem; border-radius: 7px; background: var(--red-soft); color: #991b1b; font-size: .8rem; font-weight: 700; }
    .empty, .error, .loading { display: flex; flex-direction: column; align-items: center; gap: .4rem; padding: 2rem; border-radius: 12px; background: #f8fafc; color: #64748b; text-align: center; }
    .empty strong { color: #334155; }
    .error { align-items: flex-start; border: 1px solid #fecaca; background: #fef2f2; color: #991b1b; text-align: left; }
    .error button { margin-top: .4rem; padding: .55rem .8rem; border: 0; border-radius: 7px; background: #991b1b; color: white; cursor: pointer; }
    .loading { flex-direction: row; justify-content: center; }
    .loading span { width: 9px; height: 9px; border-radius: 50%; background: #60a5fa; animation: pulse 1s infinite alternate; }
    .loading span:nth-child(2) { animation-delay: .2s; } .loading span:nth-child(3) { animation-delay: .4s; }
    .refreshing { opacity: .62; transition: opacity .15s; }
    .no-slots, .footnote { color: #64748b; font-size: .78rem; }
    .footnote { margin: .8rem .2rem 0; text-align: right; }
    @keyframes pulse { to { opacity: .25; transform: translateY(-3px); } }
    @media (max-width: 900px) { .summary-grid { grid-template-columns: repeat(2, 1fr); } .filters { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 620px) {
        .heading, .section-heading { flex-direction: column; }
        .filters, .summary-grid { grid-template-columns: 1fr; }
        .reset { width: 100%; }
        .weekday-row { grid-template-columns: 115px 1fr; }
        .weekday-total { display: none; }
        .teacher-toggle { grid-template-columns: minmax(0, 1fr) 90px 16px; gap: .5rem; padding: .8rem; }
        .date-row { grid-template-columns: 1fr; }
        .footnote { text-align: left; }
    }
</style>
